import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DailyChallenge() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [claiming, setClaiming] = useState(false);

  async function load() {
    try {
      const { data } = await axios.get(`${API}/challenge/today`, { withCredentials: true });
      setData(data);
    } catch (e) {
      setData(null);
    }
  }

  useEffect(() => { if (user) load(); }, [user]);

  if (!user || !data) return null;

  const { challenge, progress, claimed, reward, streak } = data;
  const pct = Math.min(100, Math.round((progress.current / Math.max(1, progress.target)) * 100));

  async function claim() {
    if (claiming || claimed || !progress.completed) return;
    setClaiming(true);
    try {
      const { data: res } = await axios.post(`${API}/challenge/claim`, {}, { withCredentials: true });
      toast.success(`🎯 +${res.reward} Phoenix Tokens — ${res.challenge.title} complete!`, { duration: 5000 });
      if (res.new_badges && res.new_badges.length > 0) {
        res.new_badges.forEach((badgeId, idx) => {
          setTimeout(() => {
            toast.success(`🏅 Badge Unlocked — ${badgeId}`, { duration: 5000 });
          }, 600 + idx * 700);
        });
      }
      await load();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally {
      setClaiming(false);
    }
  }

  return (
    <div
      data-testid="daily-challenge"
      className="bg-[#0F0F0F] border border-[#4C1D95]/60 p-6 mt-6"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-purple-400 font-bold mb-1 inline-flex items-center gap-1.5">
        <span>🎯</span> Daily Challenge
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="text-5xl">{challenge.icon}</div>
          <div className="min-w-0">
            <h2 className="font-display text-2xl text-white" data-testid="challenge-title">
              {challenge.title}
            </h2>
            <p className="text-sm text-neutral-400 mt-1" data-testid="challenge-desc">
              {challenge.desc}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono tabular-nums font-bold text-2xl text-[#D4AF37]">
            +{reward}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Phoenix Tokens reward</div>
          {streak && streak.current > 0 && (
            <div
              data-testid="challenge-streak"
              className="text-[10px] uppercase tracking-widest text-orange-400 mt-1 inline-flex items-center gap-1 justify-end"
            >
              <span>🔥</span> {streak.current}-day quest streak
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-[10px] uppercase tracking-widest font-bold mb-2">
          <span className="text-neutral-500">Progress</span>
          <span className={progress.completed ? "text-green-400" : "text-white"} data-testid="challenge-progress">
            {progress.current} / {progress.target}
          </span>
        </div>
        <div className="h-2 bg-[#0A0A0A] border border-zinc-900 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${progress.completed ? "bg-green-500" : "bg-[#4C1D95]"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* CTA */}
      {claimed ? (
        <div
          data-testid="challenge-claimed"
          className="bg-green-900/20 border border-green-700/40 text-green-400 text-sm font-bold uppercase tracking-widest text-center py-3"
        >
          ✓ Claimed — see you tomorrow.
        </div>
      ) : progress.completed ? (
        <button
          data-testid="challenge-claim-btn"
          onClick={claim}
          disabled={claiming}
          className="w-full bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-sm py-3 transition-all disabled:opacity-50"
        >
          {claiming ? "Claiming..." : `Claim +${reward} Phoenix Tokens`}
        </button>
      ) : (
        <div className="text-xs text-neutral-500 text-center py-2 uppercase tracking-widest">
          Keep going — finish the challenge to claim.
        </div>
      )}
    </div>
  );
}
